import { Spinner } from '@change/ds-core-ui';

import { HOMEPAGE_HREF_LANGS, HOMEPAGE_L10N_MAP } from 'config/routes-l10n';

import { withPrefetchedData } from 'src/shared/prefetch';

import type { HomepagePagePrefetchedDataProps } from 'src/app/pages/homepage/pageContext';
import {
	HomepagePageContextProvider,
	prefetchPageContext,
	prefetchPageUserContext,
} from 'src/app/pages/homepage/pageContext';

import { Layout } from '../layout';

import { HomepageErrorHandler } from './ErrorHandler';

const CHANGE_LOGO_ASSET_URL_PATH = '/favicon/favicon-400x400.png';

export function resolveLocale(locale: string) {
	return locale.replace(
		/^([a-z]{2})(-[a-z]{2})?$/i,
		(_, g1: string, g2: string) => `/${g1.toLowerCase()}${g2 ? g2.toUpperCase() : ''}`,
	);
}

export const Homepage = withPrefetchedData(
	function Homepage({ prefetchedData, prefetchedUserData }: HomepagePagePrefetchedDataProps) {
		return (
			<HomepagePageContextProvider
				prefetchedData={prefetchedData}
				prefetchedUserData={prefetchedUserData}
				errorHandler={HomepageErrorHandler}
				loadingFallback={<Spinner className="mx-auto my-64" />}
			>
				<Layout />
			</HomepagePageContextProvider>
		);
	},
	{
		prefetchName: __MODULE_HASH__,
		prefetchData: async (context) => prefetchPageContext(context),
		prefetchUserData: async (context) => prefetchPageUserContext(context),
		pageTitle: ({ i18n, fromTemplate }) => fromTemplate(i18n.translate('corgi.homepage.meta.title')),
		pageCanonical: ({ params, pathname }) => {
			const locale = params.locale || pathname.split('/')[1];
			if (locale) {
				const validL10n =
					HOMEPAGE_L10N_MAP[locale] ||
					HOMEPAGE_L10N_MAP[locale.replace(/-([a-z]+)$/i, (_, p1: string) => `-${p1.toUpperCase()}`)];
				if (validL10n) {
					return resolveLocale(locale);
				}
			}
			return '/';
		},
		pageMetadata: ({ i18n, getStaticAssetUrl }) => ({
			description: i18n.translate('corgi.homepage.meta.description'),
			ogTitle: i18n.translate('corgi.homepage.meta.og.title'),
			ogDescription: i18n.translate('corgi.homepage.meta.og.description'),
			ogImage: getStaticAssetUrl(CHANGE_LOGO_ASSET_URL_PATH),
			ogType: 'website' as const,
			twitterCard: 'summary' as const,
			twitterImage: getStaticAssetUrl(CHANGE_LOGO_ASSET_URL_PATH),
			twitterImageAlt: i18n.translate('corgi.homepage.meta.title'),
		}),
		pageSchemaOrg: ({ defineOrganization, resolveUrl }) =>
			defineOrganization({
				image: 'https://static.change.org/favicons/favicon-400x400.png',
				url: resolveUrl('/'),
				sameAs: [
					'https://www.facebook.com/change.org',
					'https://x.com/Change',
					'https://www.instagram.com/changedotorg/',
					'https://www.linkedin.com/company/change-org/',
				],
				logo: 'https://static.change.org/favicons/favicon-114x114.png',
				name: 'Change.org',
				description:
					"Change.org is the world's platform for change. On Change.org, people everywhere are starting campaigns and petitions, mobilizing supporters, and working with decision makers to drive solutions.",
				email: 'support@change.org',
				contactPoint: {
					'@type': 'ContactPoint',
					url: 'https://help.change.org/contact-us',
				},
			}),
		pageAlternateUrls: () => ({
			'en-us': '/',
			'x-default': '/',
			...Object.fromEntries(
				HOMEPAGE_HREF_LANGS.filter(({ hrefLang }) => hrefLang !== 'en-us').map(({ hrefLang, slug }) => [
					hrefLang,
					`/${slug}`,
				]),
			),
		}),
	},
);
